"use client"

import { useState } from 'react';
import Image from 'next/image';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTranslations } from "next-intl";

import TrendDescription from "@/app/[locale]/trends/TrendDescription";

import bookmakersList from "@/data/bookmakers.json"; 
import "../_betAdvise.scss"

const bookmakerMap = new Map(bookmakersList.map((b) => [b.id, b]));


export const BetAdviseItem = ({ advise }: { advise: any }) => {
  const [visibleTrends, setVisibleTrends] = useState<number | null>(null);

  const tBetTypes = useTranslations("betTypes");
  const tBets = useTranslations("bets");
  const tFilter = useTranslations("filter");
  const tCommon = useTranslations("common");

  const bookmakers = advise.totalOdds.latest
    .slice()
    .sort((a: any, b: any) => b.value - a.value)
    .map((odd: any) => bookmakerMap.get(odd.bookmakerId) || { id: odd.bookmakerId, name: "Unknown", logo: "" });

  function getBetTypeDescription(bet: any): string {
    if (bet.type.includes('under')) return tFilter('under');
    if (bet.type.includes('over')) return tFilter('over');

    const matchingTrend = bet.trends.find((trend: any) => trend.type === bet.type);

    if (bet.type === 'win') {
      return matchingTrend.team.name;
    } else if (bet.type === 'draw') {
      return tFilter('draw');
    } else if (bet.type === 'loss') {
      return matchingTrend.opponent.team.name;
    } else if (bet.type === 'btts') {
      return tCommon('yes');
    }

    return tFilter(bet.type);
  }
    
  return (
    <div className="bet-advise glass-segment mt-0 mb-4 mb-md-5">
      {/* Cabecera */}
      <div className="head d-flex justify-content-between align-items-center px-2 py-1">
        {/* <h5 className="mb-0">Placeholder Title</h5> */}
        <div className='d-flex align-items-center'>
          { advise.stake === 3 && 
            <Image
              className='me-2' 
              src="/icons/fire.gif"
              alt="fire"
              width={25}
              height={25}
              loading="lazy" 
              unoptimized
            />
          }

          { advise.status === "pending" && <span className='badge text-bg-info my-auto ms-auto ms-sm-2'>{tBets('pending', { count: 1 })}</span> }
          { advise.status === "won" && <span className='badge text-bg-success my-auto ms-auto ms-sm-2'>{tBets('won')}</span> }
          { advise.status === "lost" && <span className='badge text-bg-danger my-auto ms-auto ms-sm-2'>{tBets('lost')}</span> }
          { advise.status === "void" && <span className='badge text-bg-light my-auto ms-auto ms-sm-2'>{tBets('void')}</span> }
        </div>
        <div className="d-flex align-items-center justify-content-end col-5 hideMobile">
          {bookmakers.reverse().map((bookmaker: any) => (
            <div key={bookmaker.id} className="text-center col-2" >
              <div style={{width: '54px', height: '27px', backgroundColor: '#FDEEB0', borderRadius: '3px', margin: '0 auto'}}>
              <Image
                title={bookmaker.name}  
                alt={bookmaker.name}
                src={bookmaker.logo}
                width={50}
                height={25}
                loading="lazy" 
                style={{height: 'auto'}}
              />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bets con sus fixtures */}
      <div className="bets">
        {advise.bets.map((bet: any) => (
          <div 
            key={bet.id} 
            className="bet d-flex flex-column flex-md-row flex-wrap px-2 py-1" 
            onClick={() => setVisibleTrends((current) => (current === bet.id ? null : bet.id))
          }>
            {/* Fixture */}
            <div className="d-flex flex-column align-items-center align-items-md-start col-12 col-md-4 px-2 mb-2">
              <div className='d-flex align-items-center mb-1'>
                <span title={bet.fixture.league.countryName}>
                  <Image
                    src={`/icons/flags/${bet.fixture.league.countryCode?.toLowerCase()}.svg`}
                    alt={bet.fixture.league.countryName}
                    width={20}
                    height={15}
                    loading="lazy" 
                  />
                </span>
                <p className='mx-1'>{bet.fixture.league.countryName}</p>
                <span className='mx-1' title={bet.fixture.league.name}>
                  <Image
                    src={bet.fixture.league.logo}
                    alt={bet.fixture.league.name}
                    width={20}
                    height={20}
                    loading="lazy" 
                  />
                </span>
                <p>{bet.fixture.league.name}</p>
              </div>

              <div className='d-flex align-items-center mb-1'>
                <small className='me-1'>{bet.fixture.round}</small>
                <small className='ms-1'>{format(new Date(bet.fixture.date), 'HH:mm MM-dd', { locale: es })}</small>
              </div>

              <div className='d-flex flex-row flex-md-column align-items-start mb-1' style={{fontSize: '1.1rem'}}>
                  <div className='d-flex align-items-center'>
                    <Image
                      src={bet.fixture.homeTeam.logo}
                      alt={bet.fixture.homeTeam.name}
                      width={20}
                      height={20}
                      loading="lazy" 
                    />
                  <p className='ms-1'>{bet.fixture.homeTeam.name}</p>
                  </div>
                  <p className='hideDesktop mx-2'>vs</p>
                  <div className='d-flex align-items-center'>
                    <Image
                      src={bet.fixture.awayTeam.logo}
                      alt={bet.fixture.awayTeam.name}
                      width={20}
                      height={20}
                      loading="lazy" 
                      className='hideMobile'
                    />
                    <p className='text-end text-md-start mx-1'>{bet.fixture.awayTeam.name}</p>
                    <Image
                      src={bet.fixture.awayTeam.logo}
                      alt={bet.fixture.awayTeam.name}
                      width={20}
                      height={20}
                      loading="lazy" 
                      className='hideDesktop'
                    />
                  </div>
              </div>
            </div>

            {/* Bet */}
            <div className="d-flex flex-md-column align-items-center align-items-md-start justify-content-center justify-content-lg-start col-md-3 py-1 px-2">
              <p className="mx-2 my-auto">{tBetTypes(`${bet.betType}`)}</p>
              <strong className="mx-2 my-auto">{getBetTypeDescription(bet)}</strong>
              {bet.threshold ? <p className="mx-2 my-auto">{bet.threshold}</p> : null}
              { bet.status === "pending" && <span className='badge text-bg-info my-auto ms-auto ms-sm-2'>{tBets('pending', { count: 1 })}</span> }
              { bet.status === "won" && <span className='badge text-bg-success my-auto ms-auto ms-sm-2'>{tBets('won')}</span> }
              { bet.status === "lost" && <span className='badge text-bg-danger my-auto ms-auto ms-sm-2'>{tBets('lost')}</span> }
              { bet.status === "void" && <span className='badge text-bg-light my-auto ms-auto ms-sm-2'>{tBets('void')}</span> }
            </div>
            
            <div className="d-flex justify-content-end col-md-5">
                <p className='col-2 hideDesktop'>{tBets('odds')}</p>
                <div className='d-flex justify-content-end col-10 col-md-12'>
                {bookmakers.reverse().map((bookmaker: any) => {
                  const odd = bet.odds.latest.find(
                    (o: any) => o.bookmakerId === bookmaker.id
                  );
                  return (
                    <small key={bookmaker.id} className="text-center col-2">
                      {odd ? (
                        <>
                          {odd.value === 0 ? '–' : odd.value}
                          {odd.trend === 'up' && (
                            <span className="text-success ms-1">↑</span>
                          )}
                          {odd.trend === 'down' && (
                            <span className="text-danger ms-1">↓</span>
                          )}
                        </>
                      ) : (
                        '–'
                      )}
                    </small>
                  );
                })}
                </div>
              </div>

            {/* Tendencias */}
            {visibleTrends === bet.id && (
              <div className="bet-trend col-12 mt-1">
                {bet.trends?.map((trend: any) => (
                  <div key={trend.id} className="d-flex align-items-center p-1 mb-1">
                    <Image
                      src={trend.team.logo}
                      alt={trend.team.name}
                      width={18}
                      height={18}
                      loading="lazy" 
                    />
                    <span>{trend.team.name}</span>
                    <TrendDescription trend={trend} />
                  </div>
                ))}
              </div>
            )}
          </div>
          
        ))}
      </div>
      
      <div className="head d-flex col-12 py-1 hideDesktop">
        <div className='col-2'>

        </div>
        <div className='d-flex align-items-center justify-content-end col-10 px-2'>
        {bookmakers.reverse().map((bookmaker: any) => (
            <div key={bookmaker.id} className="text-center col-2" >
            <div style={{width: '44px', height: '24px', backgroundColor: '#FDEEB0', borderRadius: '3px', margin: '0 auto'}}>
              <Image
                title={bookmaker.name}  
                alt={bookmaker.name}
                src={bookmaker.logo}
                width={40}
                height={25}
                loading="lazy" 
                style={{height: 'auto'}}
              />
              </div>
          </div>
          ))}
        </div>
      </div>
      
      {/* Línea de totalOdds */}
      <div className="d-flex justify-content-md-end align-items-center col-12 px-2">
        <div className='col-2 text-end'>
          <small>Stake: {advise.stake}</small>
        </div>
        
        <div className="d-flex justify-content-end col-10 col-md-5">
          {bookmakers.reverse().map((bookmaker: any) => {
            const odd = advise.totalOdds.latest.find(
              (o: any) => o.bookmakerId === bookmaker.id
            );
            return (
              <span className='text-center col-2' key={bookmaker.id}>
                {odd ? (
                        <>
                          {odd.value === 0 ? '–' : odd.value}
                          {odd.trend === 'up' && (
                            <span className="text-success ms-1">↑</span>
                          )}
                          {odd.trend === 'down' && (
                            <span className="text-danger ms-1">↓</span>
                          )}
                        </>
                      ) : (
                        '–'
                      )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};
